import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import { z } from "zod"
import { cn } from "~/common/cn"
import { Button } from "~/ui/button"
import { PhotoDropzone } from "~/ui/photo-dropzone"

export const journeyLogFormSchema = z
  .object({
    completedOn: z.string().min(1, "Please pick the date you finished."),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    comment: z.string().max(500, "Comment must be 500 characters or fewer.").optional(),
    passedOn: z.boolean(),
    nextLocation: z.string().optional(),
    displayAs: z.enum(["FIRST_NAME", "ANONYMOUS"]),
  })
  .refine((data) => Boolean(data.city?.trim()) || Boolean(data.zipCode?.trim()), {
    message: "Please enter a city or zip code.",
    path: ["city"],
  })

export type JourneyLogFormValues = z.infer<typeof journeyLogFormSchema>

const inputClassName =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-400"

const textareaClassName =
  "flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"

export type JourneyLogFormSubmit = (args: {
  values: JourneyLogFormValues
  photoFile: File | null
  removeExistingPhoto: boolean
}) => Promise<void> | void

type JourneyLogFormProps = {
  defaultValues: JourneyLogFormValues
  initialPhotoUrl?: string | null
  firstName: string
  submitLabel: string
  submittingLabel: string
  submitting: boolean
  cancelTo: string
  onSubmit: JourneyLogFormSubmit
}

export const JourneyLogForm = ({
  defaultValues,
  initialPhotoUrl = null,
  firstName,
  submitLabel,
  submittingLabel,
  submitting,
  cancelTo,
  onSubmit,
}: JourneyLogFormProps) => {
  const form = useForm<JourneyLogFormValues>({
    resolver: zodResolver(journeyLogFormSchema),
    defaultValues,
  })
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [removeExistingPhoto, setRemoveExistingPhoto] = useState(false)

  const passedOn = form.watch("passedOn")
  const errors = form.formState.errors

  const visibleInitialUrl = removeExistingPhoto ? null : initialPhotoUrl
  const photoIsPresent = photoFile != null || (initialPhotoUrl != null && !removeExistingPhoto)

  const handleRemovePhoto = () => {
    if (photoFile) {
      setPhotoFile(null)
    } else if (initialPhotoUrl) {
      setRemoveExistingPhoto(true)
    }
  }

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit({ values, photoFile, removeExistingPhoto })
  })

  return (
    <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
      <div>
        <label className="text-sm font-medium">
          Date completed <span className="text-destructive">*</span>
        </label>
        <input
          type="date"
          {...form.register("completedOn")}
          className={cn(inputClassName, "mt-1")}
        />
        {errors.completedOn ? (
          <p className="mt-1 text-xs text-destructive">{errors.completedOn.message}</p>
        ) : null}
      </div>

      <fieldset>
        <legend className="text-sm font-medium">Where did you finish?</legend>
        <p className="mt-1 text-xs text-gray-500">
          Either a city/state or a zip code is fine. We use this to drop a pin on the puzzle&apos;s
          map, never your home address.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="col-span-2 sm:col-span-1">
            <label className="text-xs text-gray-500">City</label>
            <input
              {...form.register("city")}
              className={cn(inputClassName, "mt-1")}
              placeholder="Brooklyn"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="text-xs text-gray-500">State</label>
            <input
              {...form.register("state")}
              className={cn(inputClassName, "mt-1")}
              placeholder="NY"
              maxLength={2}
            />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-gray-500">Zip code (optional)</label>
            <input
              {...form.register("zipCode")}
              className={cn(inputClassName, "mt-1")}
              placeholder="11201"
              inputMode="numeric"
            />
          </div>
        </div>
        {errors.city ? (
          <p className="mt-1 text-xs text-destructive">{errors.city.message}</p>
        ) : null}
      </fieldset>

      <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 sm:p-5">
        <h2 className="text-base font-medium">Share your story (optional)</h2>
        <p className="mt-1 text-sm text-stone-600">
          Who did you do it with? What was your favorite bit? Stories like this are why people love
          seeing where these puzzles end up. Heads up: anything you write or upload here is public
          to anyone who scans the QR code, so leave out personal details you wouldn&apos;t want a
          stranger to see.
        </p>

        <div className="mt-4">
          <label className="text-sm font-medium">Your experience</label>
          <textarea
            {...form.register("comment")}
            className={cn(textareaClassName, "mt-1")}
            placeholder="Took us two snowy weekends and a lot of coffee."
            maxLength={500}
          />
          {errors.comment ? (
            <p className="mt-1 text-xs text-destructive">{errors.comment.message}</p>
          ) : null}
        </div>

        <div className="mt-4">
          <label className="text-sm font-medium">A photo (optional)</label>
          <p className="mt-1 text-sm text-stone-600">
            Selfie with the puzzle, or the finished puzzle on your table. It&apos;ll appear on the
            map next to your stop.
          </p>
          <PhotoDropzone
            file={photoFile}
            initialUrl={visibleInitialUrl}
            onFileSelected={(f) => {
              setPhotoFile(f)
              setRemoveExistingPhoto(false)
            }}
            onRemove={photoIsPresent ? handleRemovePhoto : undefined}
            label="Your journey photo"
            className="mt-3"
          />
        </div>
      </div>

      <fieldset>
        <legend className="text-sm font-medium">Did you pass it on?</legend>
        <div className="mt-2 flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              {...form.register("passedOn", {
                setValueAs: (v) => v === "true" || v === true,
              })}
              value="false"
            />
            No, still with me
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              {...form.register("passedOn", {
                setValueAs: (v) => v === "true" || v === true,
              })}
              value="true"
            />
            Yes, sent it onward
          </label>
        </div>
        {passedOn ? (
          <input
            {...form.register("nextLocation")}
            className={cn(inputClassName, "mt-3")}
            placeholder="Where is it going? (e.g. 'mailing to my sister in Boise')"
          />
        ) : null}
      </fieldset>

      <fieldset>
        <legend className="text-sm font-medium">Show me as</legend>
        <div className="mt-2 flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" {...form.register("displayAs")} value="FIRST_NAME" />
            {firstName}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" {...form.register("displayAs")} value="ANONYMOUS" />
            Anonymous
          </label>
        </div>
      </fieldset>

      <div className="flex items-center gap-4 border-t pt-6">
        <Button type="submit" disabled={submitting}>
          {submitting ? submittingLabel : submitLabel}
        </Button>
        <Link to={cancelTo} className="text-sm text-gray-500">
          Cancel
        </Link>
      </div>
    </form>
  )
}
